// TourEngine — runs AI-proposed step-by-step UI tours.
//
// Spec: docs/specs/ai-chat-guided-tour.md §6.2 (engine) + §6.3 (scroll) +
// §9 (lifecycle endpoints).
//
// Two surfaces are exported:
//
//   - TourConfirmModal: shown when AI emits a propose_tour. User
//     accepts → caller starts the run via the chatApi.startTourRun()
//     helper + mounts <TourEngine />.
//   - TourEngine: the actual runtime — SVG mask overlay, hint floating
//     box, step state machine, auto-scroll, element resolution.
//
// Phase 1 keeps the engine to the canonical scope from spec §6.2:
//   - click / input / select / navigate / review actions
//   - one-tour-at-a-time (multi-tour pause stack is OQ7's "no auto-cancel"
//     mode — paused runs persist server-side but only one runs in DOM)
//   - prefers-reduced-motion aware
//   - Escape / overlay click = pause (writes outcome='paused' server-side
//     via complete endpoint, browser closes engine, user can resume
//     from chat thread list)
//
// Spec §6.4 OQ C2 — element-tag discipline: only `data-tour-id="..."`
// elements may be targets. Non-existent ids show a one-shot retry then
// fall back to an inline error in the hint box (so AI gets useful tool
// result feedback).

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import {
  X, ArrowLeft, ChevronRight, AlertCircle,
  MousePointerClick, Type, ListChecks, Navigation, Eye,
} from "lucide-react";
import type { AiTourStep, AiTourProposal } from "../lib/chatApi.js";

// ─── Types ─────────────────────────────────────────────────────────────────

export interface TourEngineProps {
  proposal: AiTourProposal;
  /** Called when the run reaches a terminal outcome. Caller posts to
   *  /v1/ai/tours/:run_id/complete + threads/:id/tool-result.
   *  steps_completed is the count of click/input/select/navigate steps
   *  successfully resolved + transitioned. review steps are counted as
   *  completed when the user clicks "我知道了". */
  onComplete: (outcome: "completed" | "cancelled" | "failed" | "paused", info: { steps_completed: number; failure_reason?: string }) => void;
  /** Called after each step transition (step_index, outcome) so the
   *  caller can log to /v1/ai/tours/:run_id/step. */
  onStep?: (step_index: number, outcome: "completed" | "skipped" | "failed", failure_reason?: string) => void;
}

// ─── Helpers ───────────────────────────────────────────────────────────────

function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Resolve [data-tour-id="X"] with retry. Returns null if not found
 *  after 5 × 500ms. Re-checks visibility (offset width > 0). */
/** Element is "visible enough" — has non-zero size AND its bounding
 *  box overlaps the viewport. We CANNOT use `offsetParent !== null`:
 *  on iOS Safari (and inconsistently across browsers), elements inside
 *  a `position: fixed` ancestor return null. The dashboard sidebar
 *  drawer is fixed, so every nav link's `offsetParent` is null on
 *  iOS — making us silently fail to find them. getBoundingClientRect
 *  returns the post-transform layout box, which correctly handles
 *  `translate-x-(-full)` (returns rect.right ≤ 0 for off-screen). */
function isElementVisible(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  if (r.width === 0 || r.height === 0) return false;
  if (r.right <= 0 || r.bottom <= 0) return false;
  if (r.left >= window.innerWidth || r.top >= window.innerHeight) return false;
  return true;
}

async function resolveTourElement(tourId: string, signal?: AbortSignal): Promise<HTMLElement | null> {
  for (let i = 0; i < 5; i++) {
    if (signal?.aborted) return null;
    const el = document.querySelector(`[data-tour-id="${CSS.escape(tourId)}"]`);
    if (el instanceof HTMLElement && isElementVisible(el)) return el;
    await new Promise((r) => setTimeout(r, 500));
  }
  return null;
}

/** Sidebar auto-open: on mobile the dashboard sidebar is a drawer
 *  (hidden by default). If we're about to point at a `nav.*` tour-id
 *  and that element doesn't exist in DOM (or is hidden), the hamburger
 *  button needs to be clicked first. We do this transparently so the
 *  user doesn't see a "找不到 nav.locations" error and bail.
 *  Returns true if we opened the sidebar (caller should re-resolve the
 *  original target); false if no sidebar gate is needed.
 */
async function maybeOpenSidebarForNavStep(tourId: string, signal?: AbortSignal): Promise<boolean> {
  if (!tourId.startsWith("nav.")) return false;
  // Quick check: is the nav element already on-screen?
  const existing = document.querySelector(`[data-tour-id="${CSS.escape(tourId)}"]`);
  if (existing instanceof HTMLElement && isElementVisible(existing)) return false;
  // Drawer hamburger button. Header.tsx tags it `ui.sidebar.open`.
  const hamburger = document.querySelector('[data-tour-id="ui.sidebar.open"]');
  if (!(hamburger instanceof HTMLElement) || !isElementVisible(hamburger)) {
    // Desktop layout — sidebar already visible (lg:hidden hides hamburger
    // on desktop). If the nav-id still isn't reachable on desktop, this
    // is a real "element not found" condition.
    return false;
  }
  hamburger.click();
  // Wait for drawer transition (200ms in Sidebar.tsx) + a buffer.
  for (let i = 0; i < 8; i++) {
    if (signal?.aborted) return false;
    await new Promise((r) => setTimeout(r, 80));
    const retry = document.querySelector(`[data-tour-id="${CSS.escape(tourId)}"]`);
    if (retry instanceof HTMLElement && isElementVisible(retry)) return true;
  }
  return true; // we opened the sidebar; caller can retry anyway
}

/** Sticky-header offset. Pages may register
 *  `<div data-tour-header-anchor>` at the bottom of their nav; if found,
 *  we use the bottom of that element + 8px as the top scroll margin so
 *  scrollIntoView({block:"center"}) doesn't tuck the target under it. */
function topOffsetPx(): number {
  const anchor = document.querySelector("[data-tour-header-anchor]");
  if (anchor instanceof HTMLElement) {
    const r = anchor.getBoundingClientRect();
    return Math.max(0, r.bottom + 8);
  }
  return 72; // dashboard default header height
}

/** Scroll the element into view, then await scroll completion. */
async function ensureVisible(el: HTMLElement): Promise<void> {
  const behavior: ScrollBehavior = prefersReducedMotion() ? "instant" : "smooth";
  // First check if already in view (factoring sticky header).
  const rect = el.getBoundingClientRect();
  const topGuard = topOffsetPx();
  const bottomGuard = window.innerHeight - 24;
  if (rect.top >= topGuard && rect.bottom <= bottomGuard) return;

  el.scrollIntoView({ block: "center", inline: "nearest", behavior });

  // Wait for scroll to settle: rAF-poll scrollY until 3 frames stable,
  // or 800ms max. Avoids requiring the newer scrollend event.
  await new Promise<void>((resolve) => {
    let last = window.scrollY;
    let stable = 0;
    const start = performance.now();
    const tick = () => {
      const now = window.scrollY;
      if (now === last) stable += 1;
      else stable = 0;
      last = now;
      if (stable >= 3 || performance.now() - start >= 800) {
        resolve();
      } else {
        requestAnimationFrame(tick);
      }
    };
    requestAnimationFrame(tick);
  });
}

// ─── Step Action Icon ─────────────────────────────────────────────────────
// User-friendly icon per step.action — replaces the raw "[click]" /
// "[input]" prefix that leaked internal enum names to end users.

const STEP_ACTION_META: Record<AiTourStep["action"], { icon: typeof X; label: string; tone: string }> = {
  click:    { icon: MousePointerClick, label: "點擊",   tone: "text-accent" },
  input:    { icon: Type,              label: "輸入",   tone: "text-success" },
  select:   { icon: ListChecks,        label: "選擇",   tone: "text-warning" },
  navigate: { icon: Navigation,        label: "前往",   tone: "text-info" },
  review:   { icon: Eye,               label: "閱讀",   tone: "text-text-secondary" },
};

function StepActionIcon({ action }: { action: AiTourStep["action"] }): React.ReactElement {
  const meta = STEP_ACTION_META[action] ?? STEP_ACTION_META.click;
  const Icon = meta.icon;
  return (
    <span
      className={`flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded ${meta.tone}`}
      title={meta.label}
      aria-label={meta.label}
    >
      <Icon className="h-3.5 w-3.5" />
    </span>
  );
}

// ─── Confirm Modal ─────────────────────────────────────────────────────────

export function TourConfirmModal({
  proposal,
  onAccept,
  onReject,
}: {
  proposal: AiTourProposal;
  onAccept: () => void;
  onReject: () => void;
}): React.ReactElement | null {
  if (typeof document === "undefined") return null;
  return createPortal(
    <div className="fixed inset-0 z-[9100] flex items-center justify-center bg-black/60 px-4 animate-fade-in">
      <div role="dialog" aria-modal="true" className="bg-bg-surface border border-border rounded-xl shadow-2xl w-full max-w-md p-5 space-y-4">
        <header>
          <h2 className="text-lg font-semibold text-text-primary">建議引導：{proposal.title || "未命名"}</h2>
          {proposal.summary && (
            <p className="mt-1 text-sm text-text-secondary leading-relaxed">{proposal.summary}</p>
          )}
        </header>
        <div className="rounded-md border border-border bg-bg-base p-3 max-h-[40vh] overflow-y-auto">
          <p className="text-2xs text-text-muted mb-2">步驟：{proposal.steps.length} 步</p>
          <ol className="text-xs text-text-secondary space-y-1.5">
            {proposal.steps.map((s, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-5 h-5 rounded text-2xs font-mono text-text-muted bg-bg-raised border border-border">
                  {i + 1}
                </span>
                <StepActionIcon action={s.action} />
                <span className="flex-1">{s.hint || s.tour_id}</span>
              </li>
            ))}
          </ol>
        </div>
        <footer className="flex items-center justify-end gap-2">
          <button onClick={onReject} className="btn-secondary text-sm">取消</button>
          <button onClick={onAccept} className="btn-primary text-sm">
            開始引導
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ─── Tour Engine ───────────────────────────────────────────────────────────

interface Rect { x: number; y: number; w: number; h: number }

export function TourEngine({ proposal, onComplete, onStep }: TourEngineProps): React.ReactElement | null {
  const navigate = useNavigate();
  const location = useLocation();
  const [stepIndex, setStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<Rect | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [outOfView, setOutOfView] = useState(false);
  const completedRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const currentEl = useRef<HTMLElement | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const step = proposal.steps[stepIndex];

  // Cleanup all listeners + rAF on unmount.
  useEffect(() => {
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
      abortRef.current?.abort();
    };
  }, []);

  // Mid-step transition handlers — install per step, tear down on next.
  useEffect(() => {
    if (!step) return;
    setResolveError(null);
    setOutOfView(false);
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const signal = abortRef.current.signal;

    const advance = (outcome: "completed" | "skipped" | "failed", reason?: string) => {
      if (signal.aborted) return;
      onStep?.(stepIndex, outcome, reason);
      if (outcome === "completed") completedRef.current += 1;
      if (stepIndex + 1 >= proposal.steps.length) {
        onComplete("completed", { steps_completed: completedRef.current });
      } else {
        setStepIndex((i) => i + 1);
      }
    };

    const runStep = async () => {
      // navigate step: trigger router push, success when location changes
      if (step.action === "navigate") {
        // tour_id for navigate is the route path
        try {
          if (location.pathname === step.tour_id) {
            // Already there — just advance.
            advance("completed");
            return;
          }
          navigate(step.tour_id);
          // useEffect-watcher in onLocationChange below will catch the
          // pathname update + advance.
        } catch (err) {
          advance("failed", `navigate_threw:${err instanceof Error ? err.message : String(err)}`);
        }
        return;
      }

      // review step: hint box has a "我知道了" button that advances.
      if (step.action === "review") {
        // No element resolution needed; wait for user click on hint.
        const el = await resolveTourElement(step.tour_id, signal);
        if (signal.aborted) return;
        if (!el) {
          // review steps don't strictly need a target — they can stand
          // alone as commentary. Fall back to centered display.
          setTargetRect({ x: window.innerWidth / 2 - 40, y: window.innerHeight / 2 - 40, w: 80, h: 80 });
          return;
        }
        await ensureVisible(el);
        currentEl.current = el;
        setTargetRect(rectOf(el));
        return;
      }

      // click / input / select: resolve element, scroll, listen.
      let el = await resolveTourElement(step.tour_id, signal);
      if (signal.aborted) return;
      if (!el) {
        // Mobile drawer fallback: if this is a `nav.*` target and the
        // hamburger is visible (= mobile, drawer collapsed), click it
        // and retry. User shouldn't see a 找不到 error for the very
        // first nav step on mobile.
        const reopened = await maybeOpenSidebarForNavStep(step.tour_id, signal);
        if (reopened) {
          el = await resolveTourElement(step.tour_id, signal);
        }
      }
      if (signal.aborted) return;
      if (!el) {
        setResolveError(`找不到 ${step.hint || step.tour_id}（這個元素還沒準備好或不在當前畫面上，請按下方「跳過此步」繼續）`);
        return;
      }
      await ensureVisible(el);
      if (signal.aborted) return;
      currentEl.current = el;
      setTargetRect(rectOf(el));

      if (step.action === "click") {
        const onClick = () => advance("completed");
        el.addEventListener("click", onClick, { once: true });
        signal.addEventListener("abort", () => el.removeEventListener("click", onClick));
      } else if (step.action === "input") {
        const onBlur = () => {
          const val = (el as HTMLInputElement).value;
          if (val && val.length > 0) advance("completed");
        };
        el.addEventListener("blur", onBlur);
        signal.addEventListener("abort", () => el.removeEventListener("blur", onBlur));
      } else if (step.action === "select") {
        const onChange = () => advance("completed");
        el.addEventListener("change", onChange, { once: true });
        signal.addEventListener("abort", () => el.removeEventListener("change", onChange));
      }
    };

    void runStep();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, proposal.steps.length]);

  // Watch pathname for navigate-step completion.
  useEffect(() => {
    if (!step || step.action !== "navigate") return;
    if (location.pathname === step.tour_id) {
      // navigate landed — advance.
      onStep?.(stepIndex, "completed");
      completedRef.current += 1;
      if (stepIndex + 1 >= proposal.steps.length) {
        onComplete("completed", { steps_completed: completedRef.current });
      } else {
        setStepIndex((i) => i + 1);
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, stepIndex]);

  // rAF loop keeping mask + hint position in sync with the target rect
  // (handles scroll, resize, transitions).
  useEffect(() => {
    if (!currentEl.current) return;
    const el = currentEl.current;
    const tick = () => {
      const r = rectOf(el);
      setTargetRect((prev) => (prev && rectsEqual(prev, r)) ? prev : r);
      // out-of-view detection (after user manual scroll mid-step) — spec OQ6.
      const inView =
        r.y >= 0 && r.y + r.h <= window.innerHeight && r.x >= 0 && r.x + r.w <= window.innerWidth;
      setOutOfView(!inView);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [stepIndex]);

  // Esc → pause (per OQ7: paused tours persist server-side, no auto-cancel)
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onComplete("paused", { steps_completed: completedRef.current });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onComplete]);

  if (!step) return null;

  const totalSteps = proposal.steps.length;
  const hintPosition = computeHintPosition(targetRect);

  return createPortal(
    <div className="fixed inset-0 z-[9200] pointer-events-none" aria-live="polite">
      {/* Four surrounding dim rects + red outline. Crucially, the cut-out
          area in the middle has NO overlay rect, so native clicks pass
          through to the underlying element — letting the click listener
          on the target fire and auto-advance the tour. (Previous design
          used a single full-screen rect with an SVG mask; mask only
          punches a visual hole, not a hit-testing hole, so clicks were
          always consumed by the dim rect.) Dim rects have
          pointer-events-auto with NO onClick: clicks on the dim area
          are silently swallowed (nothing happens) — pause is now Esc
          or the X button only. This avoids a race where clicking the
          dim area to "exit" while editing an input would fire blur
          (advance) and dim-click (pause) in the same tick. */}
      {targetRect && (() => {
        const PAD = 4;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const x1 = Math.max(0, targetRect.x - PAD);
        const y1 = Math.max(0, targetRect.y - PAD);
        const x2 = Math.min(vw, targetRect.x + targetRect.w + PAD);
        const y2 = Math.min(vh, targetRect.y + targetRect.h + PAD);
        const dimFill = "rgba(0,0,0,0.5)";
        return (
          <svg
            width="100vw"
            height="100vh"
            className="absolute inset-0 w-full h-full pointer-events-none"
          >
            {y1 > 0 && (
              <rect x="0" y="0" width="100%" height={y1} fill={dimFill} className="pointer-events-auto" />
            )}
            {y2 < vh && (
              <rect x="0" y={y2} width="100%" height={vh - y2} fill={dimFill} className="pointer-events-auto" />
            )}
            {x1 > 0 && (
              <rect x="0" y={y1} width={x1} height={y2 - y1} fill={dimFill} className="pointer-events-auto" />
            )}
            {x2 < vw && (
              <rect x={x2} y={y1} width={vw - x2} height={y2 - y1} fill={dimFill} className="pointer-events-auto" />
            )}
            <rect
              x={x1}
              y={y1}
              width={x2 - x1}
              height={y2 - y1}
              rx="8"
              fill="none"
              stroke="rgb(239, 68, 68)"
              strokeWidth="3"
              className="pointer-events-none"
            />
          </svg>
        );
      })()}

      {/* Hint box */}
      <div
        className="absolute pointer-events-auto bg-bg-surface border border-border rounded-lg shadow-2xl p-3 max-w-[320px] animate-fade-in"
        style={hintPosition}
      >
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <span className="text-2xs text-text-muted">第 {stepIndex + 1} / {totalSteps} 步</span>
          <div className="flex items-center gap-1">
            {outOfView && currentEl.current && (
              <button
                type="button"
                onClick={() => { if (currentEl.current) void ensureVisible(currentEl.current); }}
                className="inline-flex items-center gap-1 text-2xs text-accent hover:text-accent-hover px-1.5 py-0.5 rounded bg-accent/10"
                title="回到目標元素"
              >
                <ArrowLeft className="h-3 w-3" /> 回到目標
              </button>
            )}
            <button
              type="button"
              onClick={() => onComplete("cancelled", { steps_completed: completedRef.current })}
              className="text-text-muted hover:text-text-primary p-0.5"
              aria-label="取消引導"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
        <p className="text-sm text-text-primary leading-relaxed">{step.hint}</p>
        {resolveError && (
          <p className="mt-2 text-2xs text-danger inline-flex items-start gap-1">
            <AlertCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>{resolveError}</span>
          </p>
        )}
        <div className="mt-2 flex items-center justify-end gap-2">
          {step.action === "review" && (
            <button
              type="button"
              onClick={() => {
                onStep?.(stepIndex, "completed");
                completedRef.current += 1;
                if (stepIndex + 1 >= proposal.steps.length) {
                  onComplete("completed", { steps_completed: completedRef.current });
                } else {
                  setStepIndex((i) => i + 1);
                }
              }}
              className="btn-primary text-xs px-2 py-1 inline-flex items-center gap-1"
            >
              我知道了 <ChevronRight className="h-3 w-3" />
            </button>
          )}
          {step.action !== "review" && (() => {
            // For input steps, the bottom button is an explicit "I'm
            // done filling this in" affordance — counts as completed.
            // For click/select, it's a fallback skip — counts as
            // skipped. Click steps normally auto-advance via the
            // listener on the cut-out element, but the button stays as
            // a manual override if the listener mis-fires.
            const isInputDone = step.action === "input";
            const outcome: "completed" | "skipped" = isInputDone ? "completed" : "skipped";
            const label = isInputDone ? "我已完成這個步驟" : "跳過此步";
            return (
              <button
                type="button"
                onClick={() => {
                  onStep?.(stepIndex, outcome);
                  if (outcome === "completed") completedRef.current += 1;
                  if (stepIndex + 1 >= proposal.steps.length) {
                    onComplete("completed", { steps_completed: completedRef.current });
                  } else {
                    setStepIndex((i) => i + 1);
                  }
                }}
                className={isInputDone ? "btn-primary text-xs px-2 py-1 inline-flex items-center gap-1" : "btn-secondary text-2xs px-2 py-1"}
              >
                {label}
                {isInputDone && <ChevronRight className="h-3 w-3" />}
              </button>
            );
          })()}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Inline helpers ────────────────────────────────────────────────────────

function rectOf(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  return { x: r.left, y: r.top, w: r.width, h: r.height };
}

function rectsEqual(a: Rect, b: Rect): boolean {
  return a.x === b.x && a.y === b.y && a.w === b.w && a.h === b.h;
}

/** Decide where to place the hint box relative to the target rect.
 *  Tries below first; flips to above/right/left if there isn't room. */
function computeHintPosition(target: Rect | null): React.CSSProperties {
  if (!target) {
    return { top: "20vh", left: "50%", transform: "translateX(-50%)" };
  }
  const HINT_W = 320;
  const HINT_H_EST = 140;
  const GAP = 12;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  // below
  if (target.y + target.h + GAP + HINT_H_EST < vh) {
    return {
      top: target.y + target.h + GAP,
      left: Math.max(8, Math.min(vw - HINT_W - 8, target.x)),
    };
  }
  // above
  if (target.y - GAP - HINT_H_EST > 0) {
    return {
      top: target.y - GAP - HINT_H_EST,
      left: Math.max(8, Math.min(vw - HINT_W - 8, target.x)),
    };
  }
  // right
  if (target.x + target.w + GAP + HINT_W < vw) {
    return {
      top: Math.max(8, Math.min(vh - HINT_H_EST - 8, target.y)),
      left: target.x + target.w + GAP,
    };
  }
  // left
  if (target.x - GAP - HINT_W > 0) {
    return {
      top: Math.max(8, Math.min(vh - HINT_H_EST - 8, target.y)),
      left: target.x - GAP - HINT_W,
    };
  }
  // fallback: bottom centered
  return {
    bottom: 24,
    left: "50%",
    transform: "translateX(-50%)",
  };
}
